import type {
  OutlinePolygon,
  Point,
  SingleBuildingFormState,
} from "features/dt-basic-setup/single-building/model/types";

import { useGetBuildingOutline } from "features/dt-basic-setup/single-building/api/outline";
import { STEP } from "features/dt-basic-setup/single-building/model/constants";
import { useEffectSkipFirst } from "hooks/components";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";

export const useHandleAutomaticOutlineDetection = ({
  formData,
  pauseInteraction,
  setBuildingOutline,
  stepNo,
}: {
  formData: SingleBuildingFormState;
  pauseInteraction: boolean;
  setBuildingOutline: (outline: OutlinePolygon) => void;
  stepNo: number;
}) => {
  const [shouldCallOutlineApi, setShouldCallOutlineApi] = useState(false);

  const { isError: isErrorOutline, isLoading: isLoadingOutline } = useGetBuildingOutline({
    enabled: stepNo > STEP.address && shouldCallOutlineApi && !pauseInteraction,
    lat: formData.buildingCenter.lat,
    lng: formData.buildingCenter.lng,
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!shouldCallOutlineApi || isLoadingOutline || !!formData.outline?.length) return;
    const queryData = queryClient.getQueryData([
      "buildingOutline",
      formData.buildingCenter.lat,
      formData.buildingCenter.lng,
    ]);
    if (queryData) {
      setBuildingOutline(queryData as Point[]);
      setShouldCallOutlineApi(false);
    }
  }, [
    isLoadingOutline,
    formData.buildingCenter.lat,
    formData.buildingCenter.lng,
    formData.outline?.length,
    queryClient,
    shouldCallOutlineApi,
    setBuildingOutline,
  ]);

  useEffectSkipFirst(() => {
    setBuildingOutline([]);
    setShouldCallOutlineApi(true);
  }, [formData.buildingCenter]);

  return {
    isErrorOutline,
    isLoadingOutline,
  };
};
